import axios, { type AxiosInstance, type AxiosRequestConfig, type Method } from 'axios';
import requestState from './requestState';

class BaseHttp {
  private readonly $requestKey: string;
  constructor(
    private $httpClient: AxiosInstance,
    private readonly $basePath: string,
  ) {
    this.$httpClient = $httpClient;
    this.$basePath = $basePath;
    this.$requestKey = this.constructor['name'] || '';
  }

  post<R>(url: string, data?: any, options?: BaseApiCallOptions) {
    return this.apiCall<R>('POST', url, { data }, options);
  }

  get<R>(url: string, params?: any, options?: BaseApiCallOptions) {
    return this.apiCall<R>('GET', url, { params }, options);
  }

  put<R>(url: string, data?: any, options?: BaseApiCallOptions) {
    return this.apiCall<R>('PUT', url, { data }, options);
  }

  delete<R>(url: string, data?: any, options?: BaseApiCallOptions) {
    return this.apiCall<R>('DELETE', url, { data }, options);
  }

  private async apiCall<R>(
    method: Method,
    url: string,
    requestPayload?: Pick<AxiosRequestConfig, 'data' | 'params'>,
    options?: BaseApiCallOptions,
  ): Promise<IResponse<R>> {
    const {
      requestKey,
      requestCancellation,
      ignoreCancellationError,
      skipResponseTransform,
      ...axiosOptions
    } = Object.assign({
      requestCancellation: true,
      skipResponseTransform: false,
      ignoreCancellationError: true,
    }, options || {});
    const useRequestKey = requestKey || this.$requestKey;

    try {
      if (requestCancellation) requestState.abortRequest(useRequestKey);

      const abortController = new AbortController();
      requestState.setSending(useRequestKey, abortController);

      const { data: response } = await this.$httpClient.request<BaseApiResponse<R>>({
        ...axiosOptions,
        method,
        url: `${this.$basePath}${url || '/'}`,
        data: requestPayload?.data || null,
        params: requestPayload?.params || null,
        signal: abortController.signal,
      });

      if (!axiosOptions?.skipResponseCheck) {
        this.responseErrorInterceptor(response);
      }
      this.responseOnCompleteHandler(useRequestKey);

      return this.responseTransformHandler<R>(response, skipResponseTransform);
    } catch (e: any) {
      if (axios.isCancel(e) && ignoreCancellationError) {
        // Aynı requestKey ile yeni bir istek başladığı için iptal edildi; requestState artık yeni isteğe ait.
        return { success: false, additionalFields: {}, error: REQUEST_CANCELED_ERROR, rawResponse: null } as IResponse<R>;
      }
      const response = e?.response?.data || {};
      const error = getErrorMessage(e, response);
      requestState.setError(useRequestKey, error);
      const transformedResponse = this.responseTransformHandler<R>(response, skipResponseTransform);
      return { success: false, additionalFields: transformedResponse?.additionalFields, error, rawResponse: response || e || null } as IResponse<R>;
    }
  }

  private responseOnCompleteHandler(useRequestKey: string) {
    requestState.flushState(useRequestKey);
  }

  protected responseTransformHandler<R>(response: BaseApiResponse<R>, skipResponseTransform: boolean) {
    if (response instanceof Blob || skipResponseTransform) {
      return { data: response, additionalFields: {} } as IResponse<R>;
    } else {
      const { data, ...additionalFields } = response;
      return { data, success: true, additionalFields } as IResponse<R>;
    }
  }

  protected responseErrorInterceptor(response: BaseApiResponse<any>): void {
    if (Object.prototype.hasOwnProperty.call(response, 'success') && !response.success) {
      throw Error(response.userMessage || response.message);
    }
  }
}

export default BaseHttp;

export const REQUEST_CANCELED_ERROR = 'ERR_CANCELED';

function getErrorMessage(e: any, response: any): string {
  const message = response.Message || response.message;
  if (typeof message === 'string' && message) return message;
  if (response.errors) {
    // ASP.NET doğrulama hataları { alan: [mesajlar] } şeklinde gelir.
    const errors = typeof response.errors === 'string' ? [response.errors] : Object.values(response.errors).flat();
    if (errors.length) return errors.join(' ');
  }
  if (e?.response?.status === 401) return 'Oturumunuzun süresi doldu, lütfen tekrar giriş yapın.';
  if (e?.code === 'ERR_NETWORK') return 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.';
  return e?.message || 'Bilinmeyen Hata';
}

export interface BaseApiResponse<R> {
  data: R;
  success: boolean;
  statusCode?: string;
  message?: string;
  detail: boolean;
  [key: string]: any;
}

export type BaseApiCallOptions = AxiosRequestConfig & {
  requestKey?: string;
  retryCount?: number;
  retryDelay?: number;
  unsetAuthorization?: boolean;
  skipResponseCheck?: boolean;
  skipResponseTransform?: boolean;
  requestCancellation?: boolean;
  ignoreCancellationError?: boolean;
}

export type IResponse<R> = { data?: R, success: boolean, additionalFields: Omit<BaseApiResponse<R>, 'data'>, error?: string, rawResponse?: any }
